import React from "react";
import { Link } from "react-router-dom";
import { FaServer, FaExternalLinkAlt } from "react-icons/fa";

/**
 * Fallback estático mientras el backend no tiene datos en audit_logs.
 * Se usa SOLO si el API retorna un array vacío.
 */
const DEFAULT_SERVICES = [
  { name: "API Gateway",       code: "ms-api-gateway",      status: "UNKNOWN" },
  { name: "Identidad & Auth",  code: "ms-identity-access",  status: "UNKNOWN" },
  { name: "Emissiones",         code: "ms-emisiones",        status: "UNKNOWN" },
  { name: "Validaciones",      code: "ms-validations",      status: "UNKNOWN" },
  { name: "Interoperabilidad", code: "ms-interoperability", status: "UNKNOWN" },
  { name: "Configuración",     code: "ms-config-system",    status: "UNKNOWN" },
  { name: "Build & Esquemas",  code: "ms-build",            status: "UNKNOWN" },
  { name: "Datamart",          code: "ms-datamart",         status: "UNKNOWN" },
  { name: "Asistente IA",      code: "ms-ai",               status: "UNKNOWN" },
  { name: "Notificaciones",    code: "ms-notifications",    status: "UNKNOWN" },
  { name: "NOC Monitoreo",     code: "ms-monitoring",       status: "UNKNOWN" },
];

/**
 * Normaliza un objeto ServiceHealthDto del backend al formato interno del componente.
 * El DTO puede tener { serviceName, serviceCode, status, eventsLast5min, errorRateLast5min }
 */
function normalizeService(srv) {
  // Normalizar status del backend (ACTIVE → HEALTHY visual)
  const rawStatus = (srv.status || srv.status || "UNKNOWN").toUpperCase();
  const statusMap = {
    ACTIVE:   "HEALTHY",
    HEALTHY:  "HEALTHY",
    DEGRADED: "DEGRADED",
    INACTIVE: "INACTIVE",
    UNKNOWN:  "UNKNOWN",
  };
  const status = statusMap[rawStatus] || "UNKNOWN";

  // Construir etiqueta de actividad (eventos/5min si disponible)
  let activityLabel = "";
  if (typeof srv.eventsLast5min === "number") {
    activityLabel = srv.eventsLast5min > 0
      ? `${srv.eventsLast5min} ev/5m`
      : "sin actividad";
  } else if (srv.latency) {
    activityLabel = srv.latency;
  }

  return {
    name:     srv.serviceName || srv.name || srv.code || "Servicio",
    code:     srv.serviceCode || srv.code || "",
    status,
    activity: activityLabel,
  };
}

export default function CoreServicesOverview({ services = [], collapsible = false }) {
  const raw = services && services.length > 0 ? services : DEFAULT_SERVICES;
  const displayServices = raw.map(normalizeService);

  const healthyCount  = displayServices.filter(s => s.status === "HEALTHY").length;
  const degradedCount = displayServices.filter(s => s.status === "DEGRADED").length;
  const inactiveCount = displayServices.filter(s => s.status === "INACTIVE").length;
  const isLive        = services && services.length > 0;
  const Card = collapsible ? "details" : "div";
  const CardHeader = collapsible ? "summary" : "div";

  const monitoringLink = (
    <Link
      to="/monitoring"
      className="sad-card-badge"
      style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}
    >
      <span>Consola NOC</span>
      <FaExternalLinkAlt style={{ fontSize: "0.65rem" }} />
    </Link>
  );

  const servicesContent = (
    <>
      {isLive && (
        <div className="sad-services-summary">
          <span className="sad-svc-count HEALTHY">{healthyCount} activos</span>
          {degradedCount > 0 && (
            <span className="sad-svc-count DEGRADED">{degradedCount} degradados</span>
          )}
          {inactiveCount > 0 && (
            <span className="sad-svc-count INACTIVE">{inactiveCount} inactivos</span>
          )}
        </div>
      )}

      <div className="sad-services-grid">
        {displayServices.map((srv, idx) => (
          <div key={idx} className={`sad-service-badge ${srv.status}`}>
            <div className="sad-service-meta">
              <span className={`sad-service-dot ${srv.status}`} />
              <span className="sad-service-name">{srv.name}</span>
            </div>
            <span className="sad-service-latency">
              {srv.activity || srv.status.toLowerCase()}
            </span>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <Card
      className={`sad-card${collapsible ? " sad-card-collapsible sad-core-services-collapsible" : ""}`}
      open={collapsible ? true : undefined}
    >
      <CardHeader className="sad-card-header">
        <span className="sad-card-title">
          <FaServer className="text-primary" />
          {" "}Infraestructura: {displayServices.length} Microservicios Core
          {isLive && (
            <span className="sad-live-badge">● LIVE</span>
          )}
        </span>
        {!collapsible && monitoringLink}
      </CardHeader>
      {collapsible ? (
        <div className="sad-card-collapsible-body">
          <div className="sad-card-collapsible-actions">{monitoringLink}</div>
          {servicesContent}
        </div>
      ) : servicesContent}
    </Card>
  );
}
